// src/features/editor/contexts/EditorProvider.tsx

import { useState, useMemo, useRef, useCallback, type ReactNode, startTransition } from 'react';
import { toast } from 'sonner';

// Import the context object and types from its dedicated file
import { EditorContext, type SaveState } from './EditorContext';

interface EditorProviderProps {
  children: ReactNode;
}

/**
 * The EditorProvider component. It manages the editor's state and
 * provides it to its children via the EditorContext.
 */
export function EditorProvider({ children }: EditorProviderProps) {
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasUnsavedChangesSinceManualSave, setHasUnsavedChangesSinceManualSave] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [contentHash, setContentHash] = useState<string>('');
  const [lastSavedHash, setLastSavedHash] = useState<string>('');
  const [activeProviderUploadCount, setActiveProviderUploadCount] = useState(0);
  
  // A ref to hold the current save function, registered by the active page.
  const saveActionRef = useRef<(() => Promise<void>) | null>(null);

  /**
   * Allows the active editor page to register its specific save logic with the provider.
   */
  const registerSaveAction = useCallback((saveFn: () => Promise<void>) => {
    saveActionRef.current = saveFn;
  }, []);

  const beginProviderUpload = useCallback(() => {
    setActiveProviderUploadCount((count) => count + 1);
  }, []);

  const endProviderUpload = useCallback(() => {
    setActiveProviderUploadCount((count) => Math.max(0, count - 1));
  }, []);

  /**
   * Triggers the currently registered save action, managing the global save state.
   */
  const triggerSave = useCallback(async () => {
    if (saveActionRef.current) {
      setSaveState('saving');
      try {
        await saveActionRef.current();
        
        // Batch all the success state updates together to reduce re-renders
        startTransition(() => {
          setSaveState('saved');
          setHasUnsavedChanges(false);
          setHasUnsavedChangesSinceManualSave(false);
          setLastSaveTime(new Date());
        });
      } catch (error) {
        console.error("Save failed:", error);
        toast.error((error as Error).message || "Failed to save.");
        setSaveState('error'); // Set error state on failure
      }
    } else {
      console.warn("Save triggered, but no save action is registered.");
    }
  }, [contentHash]);

  /**
   * Memoizes the context value to prevent unnecessary re-renders in consumer components.
   */
  const contextValue = useMemo(() => ({
    saveState,
    setSaveState,
    hasUnsavedChanges, 
    setHasUnsavedChanges,
    hasUnsavedChangesSinceManualSave,
    setHasUnsavedChangesSinceManualSave,
    triggerSave,
    registerSaveAction,
    lastSaveTime,
    setLastSaveTime,
    contentHash,
    setContentHash,
    lastSavedHash,
    setLastSavedHash,
    activeProviderUploadCount,
    beginProviderUpload,
    endProviderUpload,
  }), [
    saveState, hasUnsavedChanges, hasUnsavedChangesSinceManualSave, 
    triggerSave, registerSaveAction, lastSaveTime, contentHash, lastSavedHash,
    activeProviderUploadCount, beginProviderUpload, endProviderUpload
  ]);

  return (
    <EditorContext.Provider value={contextValue}>
      {children}
    </EditorContext.Provider>
  );
}
